import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

declare global {
  interface Window {
    Argyle?: {
      create: (config: ArgyleConfig) => ArgyleLinkInstance;
    };
  }
}

interface ArgyleLinkInstance {
  open: () => void;
  close: () => void;
}

interface ArgyleConfig {
  userToken: string;
  sandbox: boolean;
  flowId?: string;
  onAccountConnected?: (payload: any) => void;
  onAccountError?: (payload: any) => void;
  onDDSSuccess?: (payload: any) => void;
  onDDSError?: (payload: any) => void;
  onTokenExpired?: (updateToken: (newToken: string) => void) => void;
}

// https://docs.argyle.com/link/initialization#implementing-link-for-web
export const Argyle = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const linkInstanceRef = useRef<ArgyleLinkInstance | null>(null);

  // Decode and parse token
  let accessToken: string | null = null;
  let isProduction = false;
  let argyleFlowId: string | null = null;
  let error: Error | null = null;

  if (token) {
    try {
      const decoded = atob(token);
      const parsed = JSON.parse(decoded);
      accessToken = parsed.accessToken;
      isProduction = parsed.isProduction === true;
      argyleFlowId = parsed.argyleFlowId || null;
    } catch (e) {
      error = e as Error;
      console.error('Error decoding token:', e);
    }
  }

  useEffect(() => {
    if (!accessToken) return;
    // Load the Argyle script if not already loaded
    const loadScript = () => {
      return new Promise<void>((resolve, reject) => {
        // Check if script is already loaded
        if (window.Argyle) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://plugin.argyle.com/argyle.web.v5.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    // Initialize Argyle after script loads
    const initializeArgyle = async () => {
      try {
        await loadScript();

        if (window.Argyle && accessToken) {
          const config: ArgyleConfig = {
            userToken: accessToken,
            sandbox: !isProduction, // Set to false for production environment.
            onAccountConnected: (payload) => console.log('onAccountConnected', payload),
            onAccountError: (payload) => console.log('onAccountError', payload),
            onDDSSuccess: (payload) => console.log('onDDSSuccess', payload),
            onDDSError: (payload) => console.log('onDDSError', payload),
            onTokenExpired: (updateToken) => {
              console.log('onTokenExpired');
              // Generate a new user token.
              // updateToken(newToken)
            },
          };

          // Add flowId if provided
          if (argyleFlowId) {
            config.flowId = argyleFlowId;
          }

          const linkInstance = window.Argyle.create(config);
          linkInstanceRef.current = linkInstance;
          linkInstance.open();
        }
      } catch (error) {
        console.error('Failed to load Argyle script:', error);
      }
    };

    initializeArgyle();

    // Cleanup function
    return () => {
      if (linkInstanceRef.current) {
        // Optionally close the link instance on unmount
        // linkInstanceRef.current.close()
      }
    };
  }, [accessToken, isProduction, argyleFlowId]);

  if (!token) {
    return <div>Error: Missing token parameter</div>;
  }

  if (error) {
    return <div>Error: Invalid token format</div>;
  }

  if (!accessToken) {
    return <div>Error: Missing accessToken in token</div>;
  }

  return <div />; // Argyle handles its own UI
};

